import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import SearchBar from '../components/SearchBar';
import HotCards from '../components/HotCards';
import CardCandidateList from '../components/CardCandidateList';
import EmptySearchResult from '../components/EmptySearchResult';
import { useSearchStore } from '../stores/searchStore';
import type { ClassifyPrediction } from '../types';

export default function HomePage() {
  const navigate = useNavigate();
  const {
    mode,
    searchQuery,
    setSearchQuery,
    searchByText,
    searchByImage,
    imagePreviewUrl,
    candidates,
    totalCount,
    isLoading,
    isLoadingMore,
    error,
    clearSearch,
    loadMoreCandidates,
    hasMoreCandidates,
  } = useSearchStore();

  // 处理文字搜索
  const handleSearch = (query: string) => {
    searchByText(query);
  };

  // 处理图片上传
  const handleImageUpload = (file: File) => {
    searchByImage(file);
  };

  // 处理候选卡片选择
  const handleCandidateSelect = (prediction: ClassifyPrediction) => {
    if (prediction.card) {
      navigate(`/card/${prediction.id_label}`, {
        state: { card: prediction.card },
      });
    } else {
      // 如果没有卡片信息，使用 id_label 进行搜索
      navigate(`/card/${prediction.id_label}`, {
        state: {
          card: {
            uid: prediction.id_label,
            name: prediction.id_label,
          },
        },
      });
    }
  };

  // 渲染主内容区域
  const renderContent = () => {
    // 加载中状态
    if (isLoading && mode !== 'idle') {
      return (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-secondary mt-4">
            {mode === 'image' ? '正在识别卡片...' : '正在搜索...'}
          </p>
        </div>
      );
    }

    // 错误状态
    if (error) {
      return (
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={clearSearch}
            className="text-sm text-primary hover:underline"
          >
            返回
          </button>
        </div>
      );
    }

    // 图片识别或文字搜索结果 - 展示候选卡片列表
    if ((mode === 'image' || mode === 'text') && candidates.length > 0) {
      return (
        <CardCandidateList
          candidates={candidates}
          onSelect={handleCandidateSelect}
          onBack={clearSearch}
          onLoadMore={mode === 'text' ? loadMoreCandidates : undefined}
          hasMore={mode === 'text' ? hasMoreCandidates() : false}
          isLoadingMore={isLoadingMore}
          total={mode === 'text' ? totalCount : undefined}
        />
      );
    }

    // 搜索结果为空 - 展示空结果提示
    if ((mode === 'image' || mode === 'text') && candidates.length === 0) {
      return (
        <EmptySearchResult
          type={mode}
          onBack={clearSearch}
          onImageUpload={mode === 'image' ? handleImageUpload : undefined}
        />
      );
    }

    // 默认状态 - 展示热门卡片
    return <HotCards />;
  };

  return (
    <Layout>
      {/* 搜索区域 */}
      <div className="mb-8">
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          onSearch={handleSearch}
          onImageUpload={handleImageUpload}
          imagePreviewUrl={imagePreviewUrl}
          onClearSearch={clearSearch}
          isLoading={isLoading && mode === 'text'}
          isImageLoading={isLoading && mode === 'image'}
        />
      </div>

      {/* 主内容区域 */}
      {renderContent()}
    </Layout>
  );
}
